import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import type { IComponent } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { fetchProducts } from '../product';
import { fetchThemes } from '../themes';
import { mapComponent } from './helpers';
import { transformComponent } from './transform-helper';

export const DEFAULT_COMPONENT_INCLUDES = [
  'componentTypes',
  'layoutTypes',
  'productCategories',
  'productVariations',
  'componentTags',
  'assets',
  'themes',
  'phases',
];

// The bulk replace flow only renders component_type and theme; requesting the
// full include list adds expensive per-component joins (CC-22178).
export const BULK_REPLACE_COMPONENT_INCLUDES = ['componentTypes', 'themes'];

export const useGetComponent = (
  componentId: number | string,
  overrideQuerySetting?: UseQueryOptions<IComponent>,
  enabledTransform: boolean = false,
  includes: string[] = DEFAULT_COMPONENT_INCLUDES,
) =>
  useQuery({
    queryKey: queryKeys.components.details([
      componentId,
      `enabledTransform=${enabledTransform}`,
      `include=${includes.join(',')}`,
    ]),
    queryFn: () => fetchComponent(componentId, enabledTransform, includes),
    enabled: !isEmpty(componentId),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });

export async function fetchComponent(
  componentId: string | number,
  enabledTransform?: boolean,
  includes: string[] = DEFAULT_COMPONENT_INCLUDES,
) {
  const url = `/components/${componentId}${generateQuery(includes)}`;

  const [componentResponse, themes, products] = await Promise.all([
    requestCCApi({
      url: url,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.themes.all(),
      queryFn: () => fetchThemes(),
      staleTime: Infinity,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.products.all(),
      queryFn: () => fetchProducts(),
      staleTime: Infinity,
    }),
  ]);

  const component = mapComponent(
    componentResponse.data,
    componentResponse.includes,
  );

  let transformedComponent = component;
  if (enabledTransform) {
    transformedComponent = transformComponent(component, products, themes);
  }

  return transformedComponent;
}
